import { useEffect, useRef } from 'react';

const features = [
  { icon: 'ri-droplet-line', label: '스프레이 부스' },
  { icon: 'ri-fire-line', label: '건조로 시스템' },
  { icon: 'ri-robot-2-line', label: '자동화 도장라인' },
  { icon: 'ri-leaf-line', label: '환경 친화적 설계' },
];

export default function PaintingSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-in-paint').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 130);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="painting" className="bg-white py-0 overflow-hidden" ref={sectionRef}>
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[680px]">
        {/* Left: Content */}
        <div className="bg-[#F8F6F2] flex flex-col justify-center px-10 lg:px-16 py-20">
          <div
            className="fade-in-paint"
            style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
          >
            <span className="inline-block text-xs font-bold tracking-widest text-[#C0392B] border border-[#C0392B]/30 px-3 py-1 rounded-full mb-6">
              도장설비
            </span>
            <h2 className="text-3xl lg:text-4xl font-black text-[#1C1C1C] leading-tight mb-6">
              완벽한 마감을 위한<br />
              도장설비 시스템
            </h2>
            <p className="text-gray-600 leading-relaxed mb-8 text-sm">
              에스제이엔지니어링은 스프레이 부스, 건조로, 전처리 설비, 자동화 도장라인 등
              다양한 도장설비를 전문적으로 설계·제작합니다. 고품질 도장 품질과 생산성을
              동시에 만족시키는 최적의 솔루션을 제공합니다.
            </p>
            <p className="text-gray-600 leading-relaxed mb-10 text-sm">
              최신 환경 규제에 부합하는 친환경 도장 시스템을 개발하여 고객의 환경 경영을 지원하며,
              자동차 부품, 기계류, 가전제품 등 다양한 제품의 도장 라인에 적용되고 있습니다.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4 mb-10">
            {features.map((f, i) => (
              <div
                key={f.label}
                className="fade-in-paint flex items-center gap-3 bg-white rounded-xl px-4 py-3"
                style={{
                  opacity: 0,
                  transform: 'translateY(20px)',
                  transition: `opacity 0.6s ease ${0.15 + i * 0.1}s, transform 0.6s ease ${0.15 + i * 0.1}s`,
                }}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C0392B]/10">
                  <i className={`${f.icon} text-[#C0392B]`} />
                </div>
                <span className="text-xs font-semibold text-[#1C1C1C]">{f.label}</span>
              </div>
            ))}
          </div>

          <div
            className="fade-in-paint"
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.6s ease 0.5s, transform 0.6s ease 0.5s' }}
          >
            <button
              onClick={handleContact}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#C0392B] text-white font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-[#A93226] transition-all duration-200"
            >
              <i className="ri-mail-send-line" />
              견적 문의하기
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>

        {/* Right: Image */}
        <div className="relative overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/eb96fa4b3131d976efcfde24a7c7302d.jpg"
            alt="도장설비"
            className="w-full h-full object-cover object-top"
          />
          {/* Overlay Text */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-12 right-10 text-right">
            <p className="text-xs font-bold tracking-widest text-white/60 mb-3">PAINTING EQUIPMENT</p>
            <h3 className="text-5xl font-black text-white leading-tight">
              프리미엄<br />
              <span className="font-light">도장</span><br />
              솔루션
            </h3>
          </div>
        </div>
      </div>
    </section>
  );
}
